import { useQuery } from '@tanstack/react-query';
import { api, PaginationInfo } from '../services/api';
import type { AuditEntry, OpsStats } from '../types/domain';

export interface AuditSearchParams {
  page?: number;
  limit?: number;
  /** ISO date/datetime. Inclusive lower bound. */
  dateFrom?: string;
  /** ISO date/datetime. EXCLUSIVE upper bound — the caller must pass the
   *  instant just past the last moment to include (see AuditTrail.tsx). */
  dateTo?: string;
  actor?: string;
  action?: string;
  entityType?: string;
  ticketNumber?: string;
  correlationId?: string;
  /** Widens a ticketNumber filter to every row in the run that touched it. */
  includeRun?: boolean;
}

function toQueryString(params: AuditSearchParams): string {
  const sp = new URLSearchParams();
  if (params.page) sp.set('page', String(params.page));
  if (params.limit) sp.set('limit', String(params.limit));
  if (params.dateFrom) sp.set('dateFrom', params.dateFrom);
  if (params.dateTo) sp.set('dateTo', params.dateTo);
  if (params.actor) sp.set('actor', params.actor);
  if (params.action) sp.set('action', params.action);
  if (params.entityType) sp.set('entityType', params.entityType);
  if (params.ticketNumber) sp.set('ticketNumber', params.ticketNumber);
  if (params.correlationId) sp.set('correlationId', params.correlationId);
  if (params.includeRun) sp.set('includeRun', '1');
  return sp.toString();
}

/**
 * `getPaged<T>` — NOT `get<T>` — because `pagination` is a sibling of `data`
 * in the response envelope; the plain client silently drops it (see
 * services/api.ts).
 */
export function useAuditSearch(params: AuditSearchParams) {
  return useQuery({
    queryKey: ['audit', 'search', params],
    queryFn: () => api.getPaged<AuditEntry[]>(`/api/v1/audit-trail?${toQueryString(params)}`),
  });
}

/** The ticket detail Audit tab: every row touching this ticket, including the
 *  agent-run steps that carry no TicketId of their own (the P9 under-reporting fix). */
export function useTicketAuditTrail(ticketNumber: string | undefined) {
  return useQuery({
    queryKey: ['audit', 'ticket', ticketNumber],
    queryFn: () => api.getPaged<AuditEntry[]>(`/api/v1/audit-trail?${toQueryString({ ticketNumber, includeRun: true, limit: 200 })}`),
    enabled: !!ticketNumber,
  });
}

export function useOpsStats() {
  return useQuery({
    queryKey: ['agent', 'ops-stats'],
    queryFn: () => api.get<OpsStats>('/api/v1/agent/ops-stats'),
    refetchInterval: 60_000,
  });
}

export type { PaginationInfo };
